/**
 * The guided flow of the public onboarding page: which steps there are, in which order, what makes one
 * complete, and how a step is named in the address (`#/anlagen`). The server's `slots` decide which
 * upload steps exist; nothing about the slots is hard-coded except their order of appearance and the
 * address names of the four well-known ones.
 */
import type { DataSlot, LeadAnswers, LeadProfile, PublicLeadView } from "../OnboardingAPI";
import { companyReady, LOGO_SLOT } from "./onboardingModel";

export type StepKind = "welcome" | "company" | "properties" | "slot" | "optional" | "questions" | "phone" | "review" | "done";

export interface StepDef {
  kind: StepKind;
  /** The slot an upload step is about. */
  slot?: DataSlot;
  /** The address fragment, without `#/`. */
  slug: string;
  /** An upload step that asks for nothing: it is done without a file (the escape plans). */
  optional?: boolean;
  /** 0 for the welcome page, 1..n for the numbered steps, n+1 for the closing view. */
  index: number;
}

const SLUGS: Record<string, string> = {
  buildings: "gebaeude",
  assets: "anlagen",
  spaces: "raeume",
  floorplans: "grundrisse",
  escapePlans: "escape",
};

/** The escape and rescue plans have a step of their own, after the floor plans. */
export const ESCAPE_SLOT: DataSlot = "escapePlans";

/** The slots the "further documents" step collects: every optional one, the logo and the escape plans (own step) apart. */
export function optionalSlots(view: Pick<PublicLeadView, "slots">) {
  return view.slots.filter((slot) => !slot.required && slot.slot !== LOGO_SLOT && slot.slot !== ESCAPE_SLOT);
}

/** One step per required upload slot (in the view's order), then the optional ones together. */
export function buildSteps(view: Pick<PublicLeadView, "slots"> & { phone?: unknown }): StepDef[] {
  const steps: StepDef[] = [{ kind: "welcome", slug: "", index: 0 }, { kind: "company", slug: "unternehmen", index: 1 },
    // the kind and role of the properties: a step of its own, right after the company
    { kind: "properties", slug: "immobilien", index: 2 },
  ];
  view.slots
    .filter((slot) => slot.required && slot.slot !== LOGO_SLOT)
    .forEach((slot) => steps.push({ kind: "slot", slot: slot.slot, slug: SLUGS[slot.slot] ?? slot.slot.toLowerCase(), index: steps.length }));
  // an older server has no such slot: then there is no such step either
  if (view.slots.some((slot) => slot.slot === ESCAPE_SLOT)) {
    steps.push({ kind: "slot", slot: ESCAPE_SLOT, slug: SLUGS[ESCAPE_SLOT], index: steps.length, optional: true });
  }
  if (optionalSlots(view).length > 0) steps.push({ kind: "optional", slug: "weitere", index: steps.length });
  steps.push({ kind: "questions", slug: "fragen", index: steps.length });
  // the server checks the mobile number (the field is in the view): a step confirms it by an SMS code, just before the review
  if (view.phone) steps.push({ kind: "phone", slug: "handynummer", index: steps.length });
  steps.push({ kind: "review", slug: "pruefen", index: steps.length });
  steps.push({ kind: "done", slug: "danke", index: steps.length });
  return steps;
}

/** How many numbered steps there are ("Schritt 3 von 8"): everything between the welcome page and the closing view. */
export const numberedCount = (steps: StepDef[]) => steps.length - 2;

export function stepFromHash(hash: string, steps: StepDef[]): StepDef | undefined {
  const slug = hash.replace(/^#\/?/, "");
  return steps.find((step) => step.slug === slug);
}

export const hashFor = (step: StepDef) => (step.slug ? `#/${step.slug}` : "");

export interface Progress {
  values: { profile: LeadProfile; answers: LeadAnswers };
  view: Pick<PublicLeadView, "files" | "phone">;
  /** Required slots the person deliberately leaves empty ("Habe ich gerade nicht"); the page's own state only. */
  skipped: ReadonlySet<DataSlot>;
}

export const companyComplete = ({ values, view }: Progress) => companyReady(values, !!view.phone);

export const questionsComplete = ({ values }: Progress) => !!values.answers.stableIds && !!values.answers.labelsInstalled;

export function isComplete(step: StepDef, progress: Progress): boolean {
  switch (step.kind) {
    case "welcome":
    case "optional":
    case "done":
      return true;
    case "company":
      return companyComplete(progress);
    case "properties":
      return !!progress.values.profile.assetClass;
    case "slot":
      return !!step.optional || progress.view.files.some((file) => file.slot === step.slot) || progress.skipped.has(step.slot as DataSlot);
    case "questions":
      return questionsComplete(progress);
    case "phone":
      return !!progress.view.phone?.verified;
    case "review":
      return false;
  }
}

/**
 * The first numbered step before the review that is not complete; the review when everything is. The properties step
 * is an answer that may be passed by ("Weiter" is never held back there): it does not pull the person back to it.
 */
export function firstIncomplete(steps: StepDef[], progress: Progress): StepDef {
  const found = steps.find((step) => step.kind !== "welcome" && step.kind !== "done" && step.kind !== "properties" && !isComplete(step, progress));
  return found ?? steps.find((step) => step.kind === "review")!;
}

/** Whether the lead has been worked on: a file, an answer, or a profile field beyond what the invitation carried. */
export function hasStarted(view: PublicLeadView): boolean {
  const profile = view.profile;
  // The invitation already carries the company, the contact, the e-mail address and the asset class: those do
  // not say the person has been here. (A changed asset class goes unnoticed on purpose - the files and answers do.)
  const extra = [profile.legalName, profile.street, profile.streetNumber, profile.postalCode, profile.city, profile.country, profile.website, profile.contactPhone, profile.ownership];
  return (
    view.files.length > 0 ||
    Object.values(view.answers).some((value) => !!value) ||
    extra.some((value) => !!value)
  );
}
